/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  NotificationsRequest,
  NotificationsPayloadResponse,
  NotificationsCountPayloadResponse,
  MarkNotificationsResponse,
  MarkNotificationsRequest,
  Notification,
  FilterCriteria,
  GetNotificationPreferenceResponse,
  NotificationPreference,
} from 'src/types/Notifications';
import FetchStatus from 'src/types/FetchStatus';
import { customFetch, constructQueryParams } from 'src/utils';
import {
  NOTIFICATIONS_API_ENDPOINT,
  NOTIFICATIONS_COUNT_API_ENDPOINT,
  NOTIFICATIONS_V2_API_ENDPOINT,
  NOTIFICATION_PREFERENCES_API_ENDPOINT,
} from 'src/constants/apiEndpoints';
import { NotificationStatus } from '@amzn/cuminuicomponents';
import { AppState } from 'src/redux/store';

export interface NotificationsState {
  notifications: Array<Notification>,
  cursor: string | undefined,
  unreadCount: number,
  status: FetchStatus,
  updateStatus: FetchStatus,
  notificationCountStatus: FetchStatus,
  error?: string,
  preferencesFetchStatus: FetchStatus,
  preferencesUpdateStatus: FetchStatus,
  webNotificationPreference: NotificationPreference
}

export const initialState: NotificationsState = {
  notifications: [],
  cursor: undefined,
  unreadCount: 0,
  status: FetchStatus.idle,
  notificationCountStatus: FetchStatus.idle,
  updateStatus: FetchStatus.idle,
  preferencesFetchStatus: FetchStatus.idle,
  preferencesUpdateStatus: FetchStatus.idle,
  webNotificationPreference: {
    notificationOnGroupMemberAdditionEnabled: false,
    notificationOnPinEnabled: false,
    notificationOnNewDiscussionInGroupEnabled: false,
    notificationOnPostEnabled: false,
    notificationOnSolutionEnabled: false,
    notificationOnTagEnabled: false,
    notificationOnVoteEnabled: false,
  },
};

function sortbyUpdatedAtDescending(a: Notification, b: Notification) {
  return b.updatedAt - a.updatedAt;
}

export const fetchNotificationPreferences = createAsyncThunk(
  'app/fetchNotificationPreferences',
  async (_request, { rejectWithValue }) => customFetch(
    NOTIFICATION_PREFERENCES_API_ENDPOINT,
    undefined,
    (error: Error) => rejectWithValue(error.message),
  ) as Promise<GetNotificationPreferenceResponse>,
);

export const updateNotificationPreferences = createAsyncThunk(
  'app/updateNotificationPreferences',
  async (request: Partial<NotificationsState>, { rejectWithValue }) => customFetch(
    NOTIFICATION_PREFERENCES_API_ENDPOINT,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    (error: Error) => rejectWithValue(error.message),
  ),
);

export const fetchNotifications = createAsyncThunk(
  'app/fetchNotifications',
  async ({
    cursor = null,
    pageSize = 20,
    filterCriteria = FilterCriteria.ALL,
  }: NotificationsRequest, { rejectWithValue }) => {
    const apiEndpoint = constructQueryParams({
      cursor: cursor! && encodeURIComponent(cursor),
      pageSize,
      filterCriteria,
    }, NOTIFICATIONS_API_ENDPOINT);

    return customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
    ) as Promise<NotificationsPayloadResponse>;
  },
);

export const fetchNotificationsV2 = createAsyncThunk(
  'app/fetchNotificationsV2',
  async ({
    cursor = null,
    pageSize = 20,
    filterCriteria = FilterCriteria.ALL,
  }: NotificationsRequest, { rejectWithValue }) => {
    const apiEndpoint = constructQueryParams({
      cursor: cursor! && encodeURIComponent(cursor),
      pageSize,
      filterCriteria,
    }, NOTIFICATIONS_V2_API_ENDPOINT);
    return customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
    ) as Promise<NotificationsPayloadResponse>;
  },
);

export const markNotificationsV2 = createAsyncThunk(
  'app/markNotificationsV2',
  async (request: MarkNotificationsRequest, { rejectWithValue }) => customFetch(
    `${NOTIFICATIONS_V2_API_ENDPOINT}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    (error: Error) => rejectWithValue(error.message),
  ) as Promise<MarkNotificationsResponse>,
);

export const markNotifications = createAsyncThunk(
  'app/markNotifications',
  async (request: MarkNotificationsRequest, { rejectWithValue }) => customFetch(
    `${NOTIFICATIONS_API_ENDPOINT}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    (error: Error) => rejectWithValue(error.message),
  ) as Promise<MarkNotificationsResponse>,
);

export const fetchNotificationCount = createAsyncThunk(
  'app/fetchNotificationCount',
  async (_request, { rejectWithValue }) => customFetch(
    NOTIFICATIONS_COUNT_API_ENDPOINT,
    undefined,
    (error: Error) => rejectWithValue(error.message),
    1,
  ) as Promise<NotificationsCountPayloadResponse>,
);

const setNotifications = (
  state: NotificationsState,
  payload: NotificationsPayloadResponse,
  args: { cursor?: string | null, filterCriteria?: string },
) => {
  const {
    notifications, cursor,
  } = payload;
  const { cursor: requestCursor, filterCriteria } = args;
  const updatedNotifications = [...notifications].sort(sortbyUpdatedAtDescending);
  state.status = FetchStatus.complete;
  state.cursor = cursor;
  state.notifications = requestCursor
    ? state.notifications.concat(updatedNotifications)
    : updatedNotifications;

  // Update unread count in navbar
  if (filterCriteria === FilterCriteria.UNREAD
        && state.notifications.length > state.unreadCount) {
    state.unreadCount = state.notifications.length;
  }
};

const setNotificationCount = (
  state: NotificationsState,
  payload: NotificationsCountPayloadResponse,
) => {
  state.notificationCountStatus = FetchStatus.complete;
  state.unreadCount = payload.unreadCount;
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotificationsState: (
      state: NotificationsState,
      action: PayloadAction<NotificationsPayloadResponse>,
    ) => {
      setNotifications(state, action.payload, {});
    },
    setNotificationsCountState: (
      state: NotificationsState,
      action: PayloadAction<NotificationsCountPayloadResponse>,
    ) => {
      setNotificationCount(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsV2.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchNotificationsV2.pending, (state: NotificationsState) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchNotificationsV2.fulfilled, (state: NotificationsState, action) => {
        const { cursor, filterCriteria } = action.meta.arg;
        setNotifications(state, action.payload, { cursor, filterCriteria });
      })
      .addCase(fetchNotifications.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchNotifications.pending, (state: NotificationsState) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchNotifications.fulfilled, (state: NotificationsState, action) => {
        const { cursor, filterCriteria } = action.meta.arg;
        setNotifications(state, action.payload, { cursor, filterCriteria });
      })
      .addCase(markNotifications.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.updateStatus = FetchStatus.failed;
      })
      .addCase(markNotifications.pending, (state: NotificationsState) => {
        state.updateStatus = FetchStatus.loading;
      })
      .addCase(markNotifications.fulfilled, (state: NotificationsState, action) => {
        const { notificationIds } = action.payload;
        state.updateStatus = FetchStatus.complete;
        state.notifications.forEach((notification) => {
          if (notificationIds.includes(notification.notificationId)) {
            notification.status = NotificationStatus.Read;
          }
        });
        if (state.unreadCount) {
          let newUnreadCount = state.unreadCount - notificationIds.length;
          if (newUnreadCount < 0) {
            newUnreadCount = 0;
          }
          state.unreadCount = newUnreadCount;
        }
      })
      .addCase(markNotificationsV2.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.updateStatus = FetchStatus.failed;
      })
      .addCase(markNotificationsV2.pending, (state: NotificationsState) => {
        state.updateStatus = FetchStatus.loading;
      })
      .addCase(markNotificationsV2.fulfilled, (state: NotificationsState, action) => {
        /**
         * @todo recheck implementation on unread count for unnecessary logic
         */
        const { notificationIds } = action.payload;
        state.updateStatus = FetchStatus.complete;
        state.notifications.forEach((notification) => {
          if (notificationIds.includes(notification.notificationId)) {
            notification.status = NotificationStatus.Read;
          }
        });
        if (state.unreadCount) {
          let newUnreadCount = state.unreadCount - notificationIds.length;
          if (newUnreadCount < 0) {
            newUnreadCount = 0;
          }
          state.unreadCount = newUnreadCount;
        }
      })
      .addCase(fetchNotificationCount.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.notificationCountStatus = FetchStatus.failed;
      })
      .addCase(fetchNotificationCount.pending, (state: NotificationsState) => {
        state.notificationCountStatus = FetchStatus.loading;
      })
      .addCase(fetchNotificationCount.fulfilled, (state: NotificationsState, action) => {
        setNotificationCount(state, action.payload);
      })
      .addCase(fetchNotificationPreferences.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.preferencesFetchStatus = FetchStatus.failed;
      })
      .addCase(fetchNotificationPreferences.pending, (state: NotificationsState) => {
        state.preferencesFetchStatus = FetchStatus.loading;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state: NotificationsState, action) => {
        const { webNotificationPreference } = action.payload;
        state.preferencesFetchStatus = FetchStatus.complete;
        state.webNotificationPreference = webNotificationPreference;
      })
      .addCase(updateNotificationPreferences.rejected, (state: NotificationsState, action) => {
        state.error = action.payload as string;
        state.preferencesUpdateStatus = FetchStatus.failed;
      })
      .addCase(updateNotificationPreferences.pending, (state: NotificationsState) => {
        state.preferencesUpdateStatus = FetchStatus.loading;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state: NotificationsState, action) => {
        state.preferencesUpdateStatus = FetchStatus.complete;
        // eslint-disable-next-line max-len
        state.webNotificationPreference = action.meta.arg.webNotificationPreference as NotificationPreference;
      });
  },
});

export const selectUnreadNotifications = (state: AppState) => state.notifications.notifications
  .filter((notification) => notification.status === NotificationStatus.Unread);

export const notificationsActions = notificationsSlice.actions;

export default notificationsSlice.reducer;
